import { Space, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TFunction } from "i18next";
import type { EventTopConsumer, EventUserStatistics, EventDrinkStatistic } from "../../../services/drinkService";
import { formatAlcohol, formatNumber, formatVolume } from "./utils";

const { Text } = Typography;
type SelectedDrinkLeaderboard = {
    drink: EventDrinkStatistic;
    participants: EventTopConsumer[];
    participantMap: Map<number, EventTopConsumer>;
};

export const buildUserColumns = (
    t: TFunction,
    isMobile: boolean,
    selectedDrinkLeaderboard: SelectedDrinkLeaderboard | null
): ColumnsType<EventUserStatistics> => {
    const participantColumn: ColumnsType<EventUserStatistics>[number] = {
        title: t("eventStats.tables.participantColumns.participant"),
        dataIndex: "username",
        key: "username",
        render: (value: string) => <Text strong>{value}</Text>,
    };
    if (isMobile) return [participantColumn, {
        title: t("eventStats.tables.participantColumns.details"),
        key: "mobileDetails",
        align: "right",
        render: (_, record) => {
            const selected = selectedDrinkLeaderboard?.participantMap.get(record.userId);
            return <Space direction="vertical" size={0} style={{ width: "100%", textAlign: "right" }}>
                <Text>{t("eventStats.tables.participantColumns.entries")}: {formatNumber(record.activityCount)}</Text>
                <Text>{t("eventStats.tables.participantColumns.liquid")}: {formatVolume(record.totalVolumeMl)}</Text>
                <Text>{t("eventStats.tables.participantColumns.alcohol")}: {formatAlcohol(record.totalAlcoholGrams)}</Text>
                {selected && <Text>{t("eventStats.tables.participantColumns.selectedDrinkVolume", {
                    drink: selectedDrinkLeaderboard?.drink.drinkName,
                })}: {formatVolume(selected.totalVolumeMl)} · {formatAlcohol(selected.totalAlcoholGrams)}</Text>}
            </Space>;
        },
    }];

    const columns: ColumnsType<EventUserStatistics> = [participantColumn,
        { title: t("eventStats.tables.participantColumns.entries"), dataIndex: "activityCount", key: "activityCount", render: (value: number) => formatNumber(value), sorter: (a, b) => a.activityCount - b.activityCount, align: "right" },
        { title: t("eventStats.tables.participantColumns.liquid"), dataIndex: "totalVolumeMl", key: "totalVolumeMl", align: "right", render: formatVolume, sorter: (a, b) => a.totalVolumeMl - b.totalVolumeMl },
        { title: t("eventStats.tables.participantColumns.alcohol"), dataIndex: "totalAlcoholGrams", key: "totalAlcoholGrams", align: "right", render: formatAlcohol, sorter: (a, b) => a.totalAlcoholGrams - b.totalAlcoholGrams },
    ];
    if (selectedDrinkLeaderboard) {
        const drinkName = selectedDrinkLeaderboard.drink.drinkName;
        columns.push(
            { title: t("eventStats.tables.participantColumns.selectedDrinkVolume", { drink: drinkName }), key: "selectedDrinkVolume", align: "right", render: (_, record) => {
                const participant = selectedDrinkLeaderboard.participantMap.get(record.userId);
                return participant ? formatVolume(participant.totalVolumeMl) : "—";
            } },
            { title: t("eventStats.tables.participantColumns.selectedDrinkAlcohol", { drink: drinkName }), key: "selectedDrinkAlcohol", align: "right", render: (_, record) => {
                const participant = selectedDrinkLeaderboard.participantMap.get(record.userId);
                return participant ? formatAlcohol(participant.totalAlcoholGrams) : "—";
            } }
        );
    }
    return columns;
};
